import { useState } from 'react';
import { Space, Spin } from 'antd';
import { useParams } from 'react-router-dom';
import Segmented from 'components/new-ui/base/segmented/Segmented';
import { getDeclarationType, getPreferredPaymentMethod } from 'views/declarations/utils/declaration-utils';
import useDeclarations from 'hooks/useDeclarations';
import VatDutyTable from './components/VatDutyTable';
import VatDutySummaryFooter from './components/VatDutySummaryFooter';
import DeclarationMrnJobInfo from './components/DeclarationMrnJobInfo';
import CalculationsSummaryHeader from './components/LocalCalculationsSummaryHeader';
import CalculationsSummaryWithDiscrepancies from './components/CalculationsSummaryWithDiscrepancies';
import LinkToCustomsOnlineServices from './components/LinkToCustomsOnlineServices';
import { useVatDutyCalculations } from './useVatDutyCalculations';
import { CalculationSource } from './types';

export default function DeclarationVatAndDuty() {
    const { declaration } = useDeclarations();
    const { declarationId } = useParams<{ declarationId: string }>();
    const [calculationSource, setCalculationSource] = useState<CalculationSource>('local');
    const { loading, localData, revenueData } = useVatDutyCalculations(declarationId, calculationSource);

    const declarationType = getDeclarationType(declaration);
    const paymentMethod = getPreferredPaymentMethod(declaration);
    const hasCalculationsDiscrepancies = declaration?.taxCalculationDiffers;
    const isLocal = calculationSource === 'local';

    const summary = isLocal ? (
        <CalculationsSummaryHeader
            methodOfPayment={paymentMethod}
            totalDuty={localData.totalDuty}
            totalVat={localData.totalVat}
            totalLiability={localData.totalLiability}
        />
    ) : hasCalculationsDiscrepancies ? (
        <CalculationsSummaryWithDiscrepancies localCalculations={localData} revenueCalculations={revenueData} />
    ) : (
        <CalculationsSummaryHeader
            methodOfPayment={paymentMethod}
            totalDuty={revenueData.totalDuty}
            totalVat={revenueData.totalVat}
            totalLiability={revenueData.totalLiability}
        />
    );

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 24,
                    marginBottom: 24,
                }}
            >
                <div style={{ display: 'flex', gap: 16 }}>
                    <Segmented
                        value={calculationSource}
                        onChange={(val) => setCalculationSource(val as CalculationSource)}
                        options={[
                            { label: 'Local Calculations', value: 'local' },
                            { label: 'Revenue Returned', value: 'revenue' },
                        ]}
                    />
                    <DeclarationMrnJobInfo />
                </div>

                <LinkToCustomsOnlineServices declarationType={declarationType} />
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', marginTop: 40 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    {summary}
                    <VatDutyTable target={calculationSource} localData={localData} revenueData={revenueData} />
                    <VatDutySummaryFooter target={calculationSource} localData={localData} revenueData={revenueData} />
                </Space>
            )}
        </div>
    );
}
